import React, { useState } from 'react';
import { Receipt } from '../types';

interface ReceiptPreviewProps {
  receipt: Receipt;
  onUpdate: (receipt: Receipt) => void;
  editable?: boolean;
}

export const ReceiptPreview: React.FC<ReceiptPreviewProps> = ({
  receipt,
  onUpdate,
  editable = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedReceipt, setEditedReceipt] = useState<Receipt>(receipt);

  const handleSave = () => {
    onUpdate(editedReceipt);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedReceipt(receipt);
    setIsEditing(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Receipt Image */}
      {receipt.imageUrl && (
        <div className="aspect-w-16 aspect-h-9 bg-gray-100">
          <img
            src={receipt.imageUrl}
            alt="Receipt"
            className="w-full h-48 object-contain p-4"
          />
        </div>
      )}

      {/* Receipt Details */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Receipt Details
          </h3>
          {editable && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-coffee-600 hover:text-coffee-700 text-sm font-medium"
            >
              ✏️ Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor
              </label>
              <input
                type="text"
                value={editedReceipt.vendor}
                onChange={e =>
                  setEditedReceipt({ ...editedReceipt, vendor: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
                placeholder="Coffee shop name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Amount
              </label>
              <input
                type="number"
                step="0.01"
                value={editedReceipt.total}
                onChange={e =>
                  setEditedReceipt({
                    ...editedReceipt,
                    total: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={editedReceipt.date}
                onChange={e =>
                  setEditedReceipt({ ...editedReceipt, date: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSave}
                className="flex-1 bg-coffee-600 text-white py-2 px-4 rounded-md hover:bg-coffee-700 focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:ring-offset-2"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Vendor:</span>
              <span className="text-sm text-gray-900">{receipt.vendor}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Total:</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(receipt.total || 0)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Date:</span>
              <span className="text-sm text-gray-900">
                {receipt.date ? formatDate(receipt.date) : 'No date'}
              </span>
            </div>
          </div>
        )}

        {/* OCR Text Preview */}
        {receipt.ocrText && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-600">
                Extracted Text:
              </span>
            </div>
            <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-700 font-mono max-h-32 overflow-y-auto">
              {receipt.ocrText}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptPreview;
